import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import TrainingEnrollModal from '@/components/feature/TrainingEnrollModal';
import ConsultQrModal from '@/components/feature/ConsultQrModal';
import { trainingEvents } from '@/mocks/home';
import { trainingAgendaTemplates, trainingSpeakers } from '@/mocks/training';
import { fetchActivityInfo } from '@/services/activity';
import { adaptTrainingEvent, formatSignFee } from '@/services/adapters';
import type { ActivityDto } from '@/services/types';
import type { TrainingEventItem } from '@/types/content';
import { useRequest } from '@/hooks/useRequest';

interface SpeakerView {
  id: number | string;
  /** 医师ID（点击跳转医师详情） */
  doctorId: number;
  name: string;
  title: string;
  hospital: string;
  field: string;
  avatar: string;
}

export default function TrainingDetailPage() {
  const { id } = useParams();
  const eventId = Number(id);
  const [consultOpen, setConsultOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);

  // 培训详情（接口失败时降级为 mock 数据）
  const { data: remoteDetail, refresh: refreshDetail } = useRequest(
    () => fetchActivityInfo(eventId).then((res) => res.dataInfo),
    { fallback: null as ActivityDto | null },
  );

  const mockEvent = trainingEvents.find((item) => item.id === eventId);
  const event: TrainingEventItem | null = remoteDetail ? adaptTrainingEvent(remoteDetail) : (mockEvent ?? null);

  if (!event) {
    return (
      <PageShell>
        <PageHero
          eyebrow="会议培训"
          title="培训不存在"
          description="抱歉，未找到该场培训，可能已结束或链接有误。"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '会议培训', to: '/activities' }, { label: '培训详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-16 text-center">
          <Link
            to="/activities"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回培训中心
          </Link>
        </section>
      </PageShell>
    );
  }

  const agenda = trainingAgendaTemplates.find((item) => item.type === event.type) ?? trainingAgendaTemplates[1];
  const speakers: SpeakerView[] = remoteDetail
    ? (remoteDetail.activitySignDoctorList ?? [])
        .filter((item) => item.doctorInfo?.name)
        .map((item) => ({
          id: item.id,
          doctorId: item.doctorId,
          name: item.doctorInfo?.name ?? '',
          title: item.doctorInfo?.level ?? '',
          hospital: item.doctorInfo?.hospital ?? '',
          field: item.doctorInfo?.subjectListStr ?? '',
          avatar: item.doctorInfo?.showImgFull ?? '',
        }))
    : (mockEvent?.speakers ?? [])
        .map((name) => trainingSpeakers.find((speaker) => speaker.name === name))
        .filter((speaker): speaker is (typeof trainingSpeakers)[number] => Boolean(speaker))
        .map((speaker) => ({
          id: speaker.id,
          doctorId: speaker.id,
          name: speaker.name,
          title: speaker.title,
          hospital: speaker.hospital,
          field: speaker.field,
          avatar: speaker.avatar,
        }));

  const remaining = Math.max(event.capacity - event.enrolled, 0);
  const isFull = remaining <= 0;
  const percent = Math.min(Math.round((event.enrolled / event.capacity) * 100), 100);

  /** 当前登录用户报名单（未登录/未报名时为 0） */
  const mySignId = remoteDetail?.mySignId ?? 0;
  /** 已报名且待支付（与后端 SignPayState 对齐）：可继续支付 */
  const canContinuePay = mySignId > 0 && remoteDetail?.mySignPayState === 1;
  /** 已完成报名（免费报名或已支付） */
  const signedDone = mySignId > 0 && !canContinuePay;
  /** 报名按钮禁用（已完成报名；或未报名且名额已满） */
  const enrollDisabled = signedDone || (!canContinuePay && isFull);

  const breadcrumb = [
    { label: '首页', to: '/' },
    { label: '会议培训', to: '/activities' },
    { label: '培训详情' },
  ];

  return (
    <PageShell>
      <PageHero
        eyebrow="会议培训"
        title={event.topic}
        description={`${event.type} · 由 ${event.hospital} 主办，${event.date} 开班。`}
        image={event.image}
        breadcrumb={breadcrumb}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* 主内容 */}
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              培训简介
            </h2>
            <div className="mt-4 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{agenda.intro}</p>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                本次培训由 <strong className="text-foreground-900">{event.hospital}</strong> 主办，围绕「{event.topic}」主题展开，
                以 <strong className="text-foreground-900">{event.type}</strong> 形式聚焦临床实用能力提升，
                帮助学员在短时间内掌握可落地的诊疗思路与关键技术。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
                  {event.type}
                </span>
                <span className="px-3 py-1.5 rounded-full bg-accent-100 text-accent-900 text-xs font-medium">
                  线下集中授课
                </span>
                <span className="px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  拜师师承 · 临床带教
                </span>
              </div>
            </div>

            {/* 主讲专家 */}
            {speakers.length > 0 && (
              <>
                <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                  <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
                  主讲专家
                </h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {speakers.map((speaker) => (
                    <Link
                      key={speaker.id}
                      to={speaker.doctorId > 0 ? `/doctors/${speaker.doctorId}` : '/doctors'}
                      title={`查看 ${speaker.name} 医师详情`}
                      className="group flex flex-col items-center text-center p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="w-20 h-20 rounded-full overflow-hidden">
                        <img
                          src={speaker.avatar}
                          alt={speaker.name}
                          title={`${speaker.name} - 主讲专家`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <h3 className="mt-3 font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors">{speaker.name}</h3>
                      {speaker.title && <p className="mt-1 text-xs text-primary-600">{speaker.title}</p>}
                      <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{speaker.hospital}</p>
                      {speaker.field && (
                        <span className="mt-3 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                          {speaker.field}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* 侧栏 */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-50 p-6">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  event.type === '精研班' ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-700'
                }`}
              >
                {event.type}
              </span>

              <div className="mt-5 space-y-3.5 text-sm">
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className="ri-calendar-event-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">培训日期</p>
                    <p className="font-medium text-foreground-900">{event.date}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className="ri-map-pin-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">主办医院</p>
                    <p className="font-medium text-foreground-900 leading-snug">{event.hospital}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className="ri-money-cny-circle-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">报名费用</p>
                    <p className="font-medium text-foreground-900">
                      {event.signFee > 0 ? `${formatSignFee(event.signFee)} / 人` : '免费参与'}
                    </p>
                  </div>
                </div>
              </div>

              {/* 报名进度 */}
              <div className="mt-5 pt-5 border-t border-background-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-500">报名进度</span>
                  <span className="text-foreground-700">
                    <span className="font-semibold text-primary-600">{event.enrolled}</span> / {event.capacity} 人
                  </span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-background-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${isFull ? 'bg-foreground-400' : 'bg-primary-500'}`}
                    style={{ width: `${percent}%` }}
                  ></div>
                </div>
                <p className={`mt-2 text-xs ${isFull ? 'text-foreground-500' : 'text-accent-700'}`}>
                  {isFull ? '本场名额已满，可关注其他场次' : `剩余 ${remaining} 个名额，报名从速`}
                </p>
              </div>

              <button
                type="button"
                disabled={enrollDisabled}
                onClick={() => setEnrollOpen(true)}
                className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  enrollDisabled
                    ? 'bg-background-200 text-foreground-500 cursor-not-allowed'
                    : canContinuePay
                      ? 'bg-accent-500 hover:bg-accent-600 text-background-50 cursor-pointer'
                      : 'bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer'
                }`}
              >
                <i className={signedDone ? 'ri-checkbox-circle-line' : canContinuePay ? 'ri-wechat-pay-line' : 'ri-edit-2-line'}></i>
                {signedDone ? '已报名' : canContinuePay ? '继续支付' : isFull ? '名额已满' : '立即报名'}
              </button>
              <button
                type="button"
                onClick={() => setConsultOpen(true)}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-customer-service-2-line"></i>
                会务咨询
              </button>

              <div className="mt-5 pt-5 border-t border-background-200 space-y-2 text-xs text-foreground-500">
                <p className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-accent-600"></i>
                  主办医院官方认证
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-award-line text-accent-600"></i>
                  拜师师承 · 临床带教
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 返回入口 */}
      <section className="w-full py-10 bg-background-100 border-t border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link
            to="/activities"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回会议培训中心
          </Link>
        </div>
      </section>

      <TrainingEnrollModal
        open={enrollOpen}
        onClose={() => {
          setEnrollOpen(false);
          // 关闭后刷新报名状态，同步"继续支付/已报名"按钮态
          refreshDetail();
        }}
        activityId={event.id}
        topic={event.topic}
        date={event.date}
        hospital={event.hospital}
        signFee={event.signFee}
        initialSignId={canContinuePay ? mySignId : 0}
      />
      <ConsultQrModal open={consultOpen} onClose={() => setConsultOpen(false)} />
    </PageShell>
  );
}